import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { RIGHT_LABELS } from '@/api/onboarding'
import { checkChannelBotRights, type ChannelRightsResponse } from '@/api/polls'
import { BotPermissionsMock } from '@/polls/BotPermissionsMock'

export function BotRightsCheckModal({
  channelUUID,
  channelTitle,
  onSatisfied,
  onCancel,
  seedRights,
}: {
  channelUUID: string
  channelTitle: string
  onSatisfied: () => void
  onCancel: () => void
  seedRights?: ChannelRightsResponse | null
}) {
  const [rights, setRights] = useState<ChannelRightsResponse | null>(seedRights ?? null)
  const [checking, setChecking] = useState(!seedRights)
  const [lastCheckedAt, setLastCheckedAt] = useState<number | null>(seedRights ? Date.now() : null)
  const [lastError, setLastError] = useState<string | null>(null)
  const [now, setNow] = useState(Date.now())

  const missing = rights?.missing ?? []
  const missingLabels = useMemo(
    () => missing.map((key) => RIGHT_LABELS[key] || `«${key}»`),
    [missing],
  )

  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(tick)
  }, [])

  useEffect(() => {
    const prevBodyOverflow = document.body.style.overflow
    const prevHtmlOverflow = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevBodyOverflow
      document.documentElement.style.overflow = prevHtmlOverflow
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const runCheck = async () => {
      setChecking(true)
      const next = await checkChannelBotRights(channelUUID)
      if (cancelled) return
      setChecking(false)
      setLastCheckedAt(Date.now())
      if (!next) {
        setLastError('Не удалось проверить права. Попробуйте ещё раз после обновления прав в MAX.')
        return
      }
      setLastError(null)
      setRights(next)
      if (next.missing.length === 0) {
        onSatisfied()
      }
    }

    if (!seedRights) void runCheck()

    const timer = window.setInterval(() => {
      void runCheck()
    }, 3000)

    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [channelUUID, onSatisfied, seedRights])

  const secondsAgo = lastCheckedAt ? Math.max(0, Math.floor((now - lastCheckedAt) / 1000)) : null

  return createPortal(
    <div className="br-veil" onClick={onCancel}>
      <div className="br-modal" onClick={(e) => e.stopPropagation()}>
        <div className="br-head">
          <div>
            <h2 className="br-title">Не хватает прав бота для публикации</h2>
            <p className="br-sub">
              Откройте настройки бота в канале <b>«{channelTitle || 'канал'}»</b> в MAX и включите красные тумблеры.
              Мы обновим автоматически.
            </p>
          </div>
          <button type="button" className="br-close" onClick={onCancel} aria-label="Закрыть">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="br-body">
          <div className="br-live">
            <div className="br-live-copy">
              <span className={`br-pip ${checking ? 'is-on' : ''}`} />
              {checking ? 'Проверяем автоматически…' : `Последняя проверка ${secondsAgo ?? 0} сек назад`}
            </div>
            <button
              type="button"
              className="br-refresh"
              onClick={async () => {
                setChecking(true)
                const next = await checkChannelBotRights(channelUUID)
                setChecking(false)
                setLastCheckedAt(Date.now())
                if (!next) {
                  setLastError('Не удалось проверить права. Попробуйте ещё раз.')
                  return
                }
                setLastError(null)
                setRights(next)
                if (next.missing.length === 0) onSatisfied()
              }}
              disabled={checking}
            >
              Проверить сейчас
            </button>
          </div>

          {missingLabels.length > 0 && (
            <div className="br-missing">
              <b>Сейчас не хватает:</b> {missingLabels.join(', ')}.
            </div>
          )}

          <BotPermissionsMock granted={rights?.granted ?? []} missing={missing} />

          {lastError && <div className="br-error">{lastError}</div>}
        </div>
      </div>

      <style>{`
        .br-veil {
          position: fixed;
          inset: 0;
          z-index: 1200;
          background: rgba(15, 18, 24, 0.52);
          backdrop-filter: blur(6px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          box-sizing: border-box;
        }
        .br-modal {
          position: relative;
          width: min(640px, 100%);
          max-height: min(88vh, 920px);
          overflow: auto;
          background: white;
          border-radius: 22px;
          box-shadow: 0 24px 60px -18px rgba(15, 18, 24, 0.4);
        }
        .br-head {
          display: block;
          padding: 22px 72px 0 24px;
        }
        .br-title {
          margin: 0 0 8px;
          font-size: 22px;
          line-height: 1.12;
          color: var(--tg-ink, #111);
        }
        .br-sub {
          margin: 0;
          font-size: 14px;
          line-height: 1.55;
          color: var(--tg-mute, #5b6678);
        }
        .br-sub b { color: var(--tg-ink, #111); font-weight: 700; }
        .br-close {
          position: absolute;
          top: 18px;
          right: 18px;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          border: 0;
          background: var(--tg-surface-2, #f5f5f9);
          color: var(--tg-mute, #8f9eb0);
          cursor: pointer;
          flex-shrink: 0;
        }
        .br-close svg { width: 16px; height: 16px; }
        .br-body {
          padding: 20px 24px 24px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .br-body > .max-mock {
          margin: 0 auto;
        }
        .br-live {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 12px 14px;
          border-radius: 12px;
          background: var(--tg-surface-2, #f5f5f9);
        }
        .br-live-copy {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
          font-size: 13px;
          color: var(--tg-ink-2, #333);
        }
        .br-pip {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--tg-blue, #0080ff);
          flex-shrink: 0;
        }
        .br-pip.is-on {
          animation: br-pulse 1.6s ease-out infinite;
        }
        @keyframes br-pulse {
          0% { box-shadow: 0 0 0 0 rgba(0,128,255,0.5); }
          100% { box-shadow: 0 0 0 7px rgba(0,128,255,0); }
        }
        .br-refresh {
          border: 0;
          border-radius: 10px;
          padding: 10px 14px;
          font-family: inherit;
          font-weight: 700;
          font-size: 13px;
          cursor: pointer;
        }
        .br-refresh {
          background: var(--tg-blue-bg, #ebf2ff);
          color: var(--tg-blue-press, #0052cc);
          white-space: nowrap;
        }
        .br-refresh:disabled {
          opacity: 0.6;
          cursor: default;
        }
        .br-missing {
          padding: 12px 14px;
          border-radius: 12px;
          background: rgba(255, 64, 0, 0.08);
          color: var(--tg-ink-2, #333);
          font-size: 13px;
          line-height: 1.5;
        }
        .br-missing b { color: #c33; }
        .br-error {
          padding: 12px 14px;
          border-radius: 12px;
          background: rgba(255, 64, 0, 0.08);
          color: #c33;
          font-size: 12.5px;
          line-height: 1.5;
        }
        @media (max-width: 640px) {
          .br-veil {
            padding: 16px;
          }
          .br-modal {
            width: 100vw;
            border-radius: 18px;
          }
          .br-head {
            padding: 18px 56px 0 16px;
          }
          .br-body {
            padding: 16px;
          }
          .br-title {
            font-size: 18px;
            line-height: 1.15;
          }
          .br-sub {
            font-size: 13.5px;
            line-height: 1.5;
          }
          .br-close {
            top: 14px;
            right: 14px;
            width: 34px;
            height: 34px;
          }
          .br-live {
            flex-direction: column;
            align-items: stretch;
          }
          .br-refresh,
          .br-refresh {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  , document.body)
}
